<template>
  <ModalCard
    is-responsive
    :is-scrollable="isScrollable"
    :close-in-modal="showCloseInModal"
    :close-confirm-fn="closeConfirmFn"
    :data-e2e="ModalCypressAttribute.modals.CONFIRM_MODAL"
  >
    <template #header v-if="hasHeader">
      <div v-if="!$slots.header" class="is-full-width">
        <div
          v-if="title"
          class="has-text-centered is-full-width is-size-3 has-text-weight-medium"
          :data-e2e="ModalCypressAttribute.views.TITLE"
        >
          {{ title }}
        </div>
      </div>
      <slot v-else name="header"></slot>
    </template>
    <template #body>
      <slot name="body">
        <p
          class="subtitle has-text-black has-text-centered is-full-width is-size-4"
          v-dompurify-html="subtitle"
        ></p>
      </slot>
    </template>
    <template #footer>
      <div
        v-if="!$slots.footer"
        :class="footerClasses"
        :data-e2e="ModalCypressAttribute.views.FOOTER_CONTAINER"
      >
        <div>
          <button
            v-if="!hideCancel"
            :class="`button is-primary is-light ${shouldPlaceButtonsAsColumn ? 'mr-0' : ''}`"
            type="button"
            :disabled="isCancelButtonDisabled"
            @click="onCloseClicked()"
            :data-e2e="ModalCypressAttribute.buttons.CANCEL"
          >
            <span class="has-text-weight-semibold">{{ cancelButtonText }}</span>
          </button>
          <slot name="extra-cancel-button" />
        </div>
        <ButtonView
          v-if="!hideConfirm"
          :class="`button ${shouldPlaceButtonsAsColumn ? 'mb-2' : ''}`"
          :type="confirmButtonType"
          :disabled="isPrimaryButtonDisabled"
          :loading="isLoading"
          @click="onConfirmClicked()"
          :data-e2e="ModalCypressAttribute.buttons.CONFIRM"
        >
          <span class="has-text-weight-semibold">{{ primaryButtonText }}</span>
        </ButtonView>
      </div>
      <slot v-else name="footer"></slot>
    </template>
  </ModalCard>
</template>

<script>
import ModalCard from '/@/modules/core/ui/components/ModalCard.vue';
import ToastService from '/@/modules/core/ui/services/ToastService';
import { ModalMixin } from '/@/modules/core/ui/mixins/ModalMixin';
import { ModalCypressAttribute } from '/@/modules/core/app/constants/ModalCypressAttribute';
import Lang from '/@/modules/core/app/services/Lang';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { ResponsiveMixin } from '@/modules/core/ui/mixins/ResponsiveMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ConfirmModal',
  slotsDefinition: {
    header: {},
    body: {},
    extraCancelButton: {},
    footer: {}
  },
  mixins: [ModalMixin, ResponsiveMixin],
  components: { ButtonView, ModalCard },
  props: {
    title: {
      type: String
    },
    subtitle: {
      type: String
    },
    hasHeader: {
      type: Boolean,
      default: true
    },
    hideCancel: {
      type: Boolean,
      default: false
    },
    hideConfirm: {
      type: Boolean,
      default: false
    },
    cancelFn: {
      type: Function,
      default: () => {}
    },
    errorFn: {
      type: Function
    },
    confirmFn: {
      type: Function,
      default: () => {}
    },
    confirmButtonType: {
      type: String,
      default: 'is-primary'
    },
    primaryButtonText: {
      type: String,
      default: () => Lang.t('core.yes-continue')
    },
    cancelButtonText: {
      type: String,
      default: () => Lang.t('core.no-cancel')
    },
    isPrimaryButtonDisabled: {
      type: Boolean,
      default: false
    },
    isCancelButtonDisabled: {
      type: Boolean,
      default: false
    },
    isScrollable: {
      type: Boolean,
      default: true
    },
    canShowCloseInModal: {
      type: Boolean,
      default: false
    },
    closeConfirmFn: {
      type: Function,
      default: undefined
    }
  },
  static: {
    ModalCypressAttribute,
    buttonsAsColumnAt: '620px'
  },
  data() {
    return {
      isLoading: false,
      shouldPlaceButtonsAsColumn: false
    };
  },
  computed: {
    showCloseInModal() {
      return this.isMobile || this.canShowCloseInModal;
    },
    footerClasses() {
      if (this.hideCancel && !this.$slots['extra-cancel-button']) {
        return ['is-flex-centered', 'is-full-width', 'px-3'];
      }

      const classes = ['is-flex', 'is-full-width', 'is-justify-content-space-between', 'px-3'];

      classes.push(
        this.shouldPlaceButtonsAsColumn
          ? 'is-flex-direction-column-reverse'
          : 'is-justify-content-center px-3'
      );

      return classes;
    }
  },
  methods: {
    async onConfirmClicked() {
      this.isLoading = true;
      try {
        await this.confirmFn(this);
      } catch (e) {
        if (this.errorFn) {
          return this.errorFn?.(e);
        }
        ToastService.show(e.message);
      } finally {
        this.isLoading = false;
      }
    },
    onCloseClicked() {
      this.cancelFn(this);
      this.close();
    },
    calculateShouldPlaceButtonsAsColumn() {
      this.shouldPlaceButtonsAsColumn = window.matchMedia(
        `(max-width: ${this.buttonsAsColumnAt})`
      ).matches;
    }
  },
  mounted() {
    this.calculateShouldPlaceButtonsAsColumn();
    this.$nextTick(() => {
      window.addEventListener('resize', this.calculateShouldPlaceButtonsAsColumn);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateShouldPlaceButtonsAsColumn);
  }
};
</script>

<style scoped>
@media (max-width: CE_MOBILE) {
  :deep(.modal-card-body.no-header) {
    margin-top: var(--ce-60px) !important;
  }
}
</style>
